/* eslint-disable no-restricted-syntax */
import React, { useEffect, useMemo, useState } from 'react';
import { withTheme } from 'styled-components';
import * as Yup from 'yup';
import queryString from 'query-string';

import { Link, useLocation, useHistory } from 'react-router-dom';
import { Button } from '@cloudez/cloudez-design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faEyeSlash,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useDocTitle } from 'hooks/docTitle';
import { useLayout } from 'hooks/layout';
import { useAuth } from 'hooks/auth';
import ReactLoading from 'react-loading';
import { CustomInput } from 'components/NewComponents';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import md5 from 'md5';

import { Card, Container, Field, Footer, Header, RememberMe } from './styles';

const schema = Yup.object().shape({
  email: Yup.string().required('Informe seu e-mail'),
  password: Yup.string().required('Informe sua senha'),
});

const SignIn = () => {
  const location = useLocation();
  const [params, setParams] = useState({});
  const theme = useLayout();
  const { signIn, signInError, loading } = useAuth();
  const [storedEmail, setStoredEmail] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  // só o e-mail fica no navegador; a senha fica com o gerenciador de senhas
  useEffect(() => {
    const email = localStorage.getItem('email');
    setStoredEmail(email);

    if (email) {
      setRememberMe(true);
      setValue('email', email);
    }
  }, [setValue]);

  const avatar = useMemo(() => {
    if (storedEmail) {
      return `https://www.gravatar.com/avatar/${md5(storedEmail)}?&d=mp&s=300`;
    }
    return `https://www.gravatar.com/avatar/?d=mp&s=300`;
  }, [storedEmail]);

  const history = useHistory();

  useDocTitle(`Login — ${theme.name}`);

  useEffect(() => {
    const _params = queryString.parse(location.search);
    const newParams = {} as any;

    for (const key in _params) {
      if (key.startsWith('utm_')) {
        newParams[key] = _params[key];
      }
    }

    const utmReferrer = document.referrer;
    if (!newParams.utm_referrer && utmReferrer) {
      newParams.utm_referrer = utmReferrer;
    }

    if (_params.reseller === 'true') {
      newParams.reseller = 'true';
    }

    setParams(newParams);
  }, []);

  const onSubmit = data => {
    if (rememberMe) {
      localStorage.setItem('email', data.email);
    } else {
      localStorage.removeItem('email');
    }

    signIn(data.email, data.password, params);
  };

  useEffect(() => {
    if (
      signInError === 'User has 2FA enabled' ||
      signInError === 'Verification code sent in email'
    ) {
      history.push('login/2fa');
    }
  }, [signInError]);

  const [showPw, setShowPw] = useState(false);

  return (
    <Container>
      {theme.loading ? (
        <ReactLoading type="balls" />
      ) : (
        <>
          <img className="logo" src={theme.logo_primary} alt={theme.name} />
          <Card>
            <Header>
              <img src={avatar} alt="UserAvatar" />
              <div>
                <h1>Acesse sua conta</h1>
                <p>Gerencie seus serviços em um só lugar</p>
              </div>
            </Header>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Field>
                <CustomInput
                  autoFocus
                  block
                  id="email"
                  name="email"
                  label="E-mail:"
                  register={register}
                  error={errors?.email?.message}
                  autoComplete="username"
                  height="48px"
                />
              </Field>
              <Field>
                <CustomInput
                  block
                  id="password"
                  name="password"
                  register={register}
                  error={errors?.password?.message}
                  type={showPw ? 'text' : 'password'}
                  icon={{
                    svg: !showPw ? faEye : faEyeSlash,
                    action: () => setShowPw(!showPw),
                    isRight: true,
                  }}
                  label="Senha:"
                  autoComplete="current-password"
                  height="48px"
                />
              </Field>

              <RememberMe>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                Lembrar-me
              </RememberMe>

              <Button
                disabled={loading}
                icon
                width="100%"
                height="48px"
                type="submit"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Entrar'}
              </Button>
            </form>

            <Footer>
              {!theme.has_disabled_register && (
                <Link to="/register">QUERO CRIAR MINHA CONTA</Link>
              )}
              <Link to="/password/reset">NÃO LEMBRO MINHA SENHA</Link>
              <Link to="/invoices/resend">SOLICITAR 2º VIA DE FATURAS</Link>
            </Footer>
          </Card>
        </>
      )}
    </Container>
  );
};

export default withTheme(SignIn);
